import { useState, useEffect, useContext } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    List,
    ListItemText,
    ListItemButton,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AppContext, SttList } from '../all';
import { formatPriceByLocale } from '../../lib/utils';
import SttValidatedForm from '../SttValidatedForm';

type Price = {
    id: number;
    name: string;
    price: number;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type Props = {
    prices: Price[];
    onAdd?: (price: Price) => void;
    onDelete?: (price: Price) => void;
};

/**
 *    Price list
 *    =========
 *
 *    Props
 *
 *        * array prices
 *        * function onAdd
 *        * function onDelete(price)
 */
export function SttPriceList({
    prices,
    onAdd,
    onDelete,
    ...rest
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [selectingPriceOpen, setSelectingPriceOpen] = useState(false);
    const [allPrices, setAllPrices] = useState<Price[]>();
    const [filters, setFilters] = useState<{ name?: string }>({});
    const form = useForm();

    /**
     * Open adding price
     */
    const openAddingPrice = () => {
        setSelectingPriceOpen(true);
    };

    /**
     * Load all prices
     */
    useEffect(() => {
        // Fetch all prices on the sports center
        if (selectingPriceOpen) {
            setAllPrices(undefined);
            cxt?.api('GET', `/scs/${cxt?.sc?.id}/rates`, {
                params: { rows: 200, ...filters },
                success: (r: { rows: Price[] }) => {
                    setAllPrices(r.rows);
                },
            });
        }
    }, [filters, cxt, selectingPriceOpen]);

    /**
     * Select price
     */
    const selectPrice = (price: Price) => {
        setSelectingPriceOpen(false);
        onAdd?.(price);
    };

    // -----| Render |-----
    return (
        <>
            <SttList
                title={t('price.prices')}
                onAdd={onAdd ? () => openAddingPrice() : undefined}
                onDelete={onDelete}
                data={
                    prices &&
                    prices.map((price: any) => ({
                        caption: (
                            <>
                                {price.name} -{' '}
                                {formatPriceByLocale(
                                    price.price,
                                    price?.priceShape?.currency ??
                                        cxt?.sc?.city?.country?.currency ??
                                        undefined,
                                    price?.priceShape?.locale,
                                )}
                            </>
                        ),
                        objToSelect: price,
                    }))
                }
                {...rest}
            />

            <Dialog
                onClose={() => setSelectingPriceOpen(false)}
                open={selectingPriceOpen}
            >
                <DialogTitle>{t('price.prices')}</DialogTitle>

                <Box px={2} pb={1}>
                    <SttValidatedForm
                        form={form}
                        fields={[
                            {
                                type: 'textInput',
                                name: 'name',
                                caption: t('price.price'),
                                onEnterKey: (value: any) =>
                                    setFilters({ name: value }),
                            },
                        ]}
                    />
                </Box>

                <List>
                    {allPrices?.map((price: any, idx: any) => (
                        <ListItemButton
                            key={idx}
                            onClick={() => selectPrice(price)}
                        >
                            <ListItemText primary={price.name} />
                        </ListItemButton>
                    ))}
                </List>
            </Dialog>
        </>
    );
}
